import { IAnalyticsService } from "../../platform/analytics/common/analytics.js";
import { ICommandService } from "../../platform/commands/common/commands.js";
import { ILogService } from "../../platform/log/common/log.js";
import { AIToolCallResponse } from "../../platform/ai/common/ai.js";
import { AIToolService } from "../../platform/ai/common/aiToolService.js";

/**
 * The tool service the HEADLESS agent registers: same behaviour, plus one
 * analytics event per call.
 *
 * Nothing else reports tool use on this path. Every `ai_chat:*` event comes from
 * ChatAI, which the agent does not register, so the preview surface could be
 * used all day and only page views and errors would arrive — not whether it is
 * used, which tools, or whether the calls succeed.
 *
 * Agent-only on purpose: in the browser ChatAI already logs each call, and a
 * second event from the shared service would double every number.
 *
 * The event carries the tool NAME, how long it took, and whether it worked.
 * Never the arguments or the result — those hold the user's scene and their own
 * words.
 */
export class AgentAIToolService extends AIToolService {

    constructor(
        @ILogService logger: ILogService,
        @ICommandService commands: ICommandService,
        @IAnalyticsService private readonly analytics: IAnalyticsService | null,
    ) {
        super(logger, commands);
    }

    override async callTool(toolName: string, parameters: object): Promise<AIToolCallResponse> {
        const startedAt = Date.now();
        try {
            const response = await super.callTool(toolName, parameters);
            this.analytics?.logEvent("agent:tool_call", {
                tool_name: toolName,
                duration_ms: Date.now() - startedAt,
                success: true,
            });
            return response;
        }
        catch (err: unknown) {
            // includes "tool not found" — a model repeatedly asking for something
            // this surface does not have is exactly what we want to see
            this.analytics?.logEvent("agent:tool_call", {
                tool_name: toolName,
                duration_ms: Date.now() - startedAt,
                success: false,
                error: (err instanceof Error ? err.message : String(err)).substring(0, 300),
            });
            throw err;
        }
    }
}
